import { useEffect, useState } from 'react';
import { Alert, Button, Form, Modal, Spinner } from 'react-bootstrap';
import { useNavigate } from 'react-router-dom';
import { FaUsers, FaEdit, FaKey } from 'react-icons/fa';
import { formatPhoneWithCountryCode } from '../../utils/phoneFormatter';

import '../../styles/ViewCompanies.css';

const BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:3000';

export default function ViewCompanies() {
  const [companies, setCompanies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [errorMsg, setErrorMsg] = useState('');
  
  // License modal states
  const [showLicenseModal, setShowLicenseModal] = useState(false);
  const [selectedCompany, setSelectedCompany] = useState(null);
  const [licenseData, setLicenseData] = useState({ category_count: 0 });
  const [licenseLoading, setLicenseLoading] = useState(false);
  const [licenseError, setLicenseError] = useState('');
  const [licenseSuccess, setLicenseSuccess] = useState('');

  const navigate = useNavigate();

  // Format address from separate fields into a single column with proper formatting
  const formatAddress = (company) => {
    const line1 = company.address_line1 || '';
    const line2 = company.address_line2 || '';
    const city = company.city || '';
    const state = company.state || '';
    const country = company.country || '';
    const postalCode = company.postal_code || '';

    const addressLines = [];
    
    // Line 1: address_line1 and address_line2
    const streetAddress = [line1, line2].filter(Boolean).join(', ');
    if (streetAddress) addressLines.push(streetAddress);
    
    // Line 2: city, state
    const cityState = [city, state].filter(Boolean).join(', ');
    if (cityState) addressLines.push(cityState);
    
    // Line 3: country, postal_code
    const countryPostal = [country, postalCode].filter(Boolean).join(' ');
    if (countryPostal) addressLines.push(countryPostal);

    return addressLines.length > 0 ? addressLines : ['N/A'];
  };

  const getAuthHeaders = () => {
    const token = localStorage.getItem('authToken');
    return {
      'Authorization': `Bearer ${token}`,
      'Content-Type': 'application/json'
    };
  };

  useEffect(() => {
    const fetchCompanies = async () => {
      try {
        const token = localStorage.getItem('authToken');
        const headers = {};
        
        if (token) {
          headers.Authorization = `Bearer ${token}`;
        }

        // Use the new license endpoint that includes company and license data
        const response = await fetch(`${BASE_URL}/api/admin/license/all`, {
          headers: headers
        });
        const data = await response.json();

        if (response.ok && data.success) {
          setCompanies(data.companies);
        } else {
          setErrorMsg(data.message || 'Failed to fetch companies list');
        }
      } catch (error) {
        setErrorMsg('Server error. Please try again later.');
        console.error('Error fetching companies list:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchCompanies();
  }, []);



  const handleViewAdmins = (companyCode) => {
    navigate(`/vcm-admin/view-company-admins/${companyCode}`);
  };

  const handleManageLicense = (company) => {
    setSelectedCompany(company);
    setLicenseData({ category_count: company.category_count || 0 });
    setLicenseError('');
    setLicenseSuccess('');
    setShowLicenseModal(true);
  };

  const handleLicenseSubmit = async (e) => {
    e.preventDefault();
    setLicenseLoading(true);
    setLicenseError('');
    setLicenseSuccess('');

    try {
      const response = await fetch(`${BASE_URL}/api/admin/license/add-license`, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify({
          company_code: selectedCompany.company_code,
          category_count: parseInt(licenseData.category_count)
        })
      });

      const data = await response.json();

      if (response.ok && data.success) {
        setLicenseSuccess(data.message);
        
        // Update the companies list to reflect the new license data
        setCompanies(prev => prev.map(company => 
          company.company_code === selectedCompany.company_code 
            ? { ...company, category_count: parseInt(licenseData.category_count) }
            : company
        ));

        // Close modal after a short delay
        setTimeout(() => {
          setShowLicenseModal(false);
        }, 1500);
      } else {
        setLicenseError(data.message || 'Failed to update license');
      }
    } catch (error) {
      console.error('Error updating license:', error);
      setLicenseError('Server error. Please try again.');
    } finally {
      setLicenseLoading(false);
    }
  };

  const closeLicenseModal = () => {
    setShowLicenseModal(false);
    setSelectedCompany(null);
    setLicenseData({ category_count: 0 });
    setLicenseError('');
    setLicenseSuccess('');
  };

  return (
    <div className="vcm-admin-view-com-container mt-4">
      <div className="d-flex justify-content-between align-items-center mb-3 vcm-admin-view-com-header">
        <h1 className="vcm-admin-title m-0">Companies</h1>
        <div>
          <button
            className="btn-custom-primary"
            onClick={() => navigate("/vcm-admin/register-company")}
          >
            Add Company
          </button>
        </div>
      </div>

      {errorMsg && (
        <Alert variant="danger" className="mt-3">
          {errorMsg}
        </Alert>
      )}

      {!loading && !errorMsg && (
        <div className="vcm-companies">
          <table className="table table-striped">
            <thead>
              <tr>
                <th>#</th>
                <th>Company Code</th>
                <th>Company Name</th>
                <th>Primary Selling Categories</th>
                <th>Address</th>
                <th>Phone</th>
                <th>Email</th>
                <th>Currency</th>
                <th>Logo</th>
                <th>License (Categories)</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {companies.map((company, index) => (
                <tr key={company.company_id || company.id || index}>
                  <td>{index + 1}</td>
                  <td> {company.company_code} </td>
                  <td>{company.company_name}</td>
                  <td>{company.primary_category_names || "-"}</td>
                  <td title={formatAddress(company).join(' - ')}>
                    <div style={{ whiteSpace: 'pre-line' }}>
                      {formatAddress(company).map((line, idx) => (
                        <div key={idx}>{line}</div>
                      ))}
                    </div>
                  </td>
                  <td>
                    {formatPhoneWithCountryCode(
                      company.company_country_code,
                      company.company_phone,
                    )}
                  </td>
                  <td>{company.company_email || "N/A"}</td>
                  <td>{company.currency}</td>
                  <td>
                    {company.company_logo ? (
                      <>
                        <img
                          src={`${BASE_URL}/uploads/company_logos/${company.company_logo}`}
                          alt="Company Logo"
                          className="vcm-company-logo"
                          onError={(e) => {
                            e.target.style.display = "none";
                            const placeholder = e.target.nextSibling;
                            if (placeholder) {
                              placeholder.style.display = "inline-block";
                            }
                          }}
                        />
                        <span
                          className="vcm-logo-placeholder"
                          style={{ display: "none" }}
                        >
                          N/A
                        </span>
                      </>
                    ) : (
                      <span className="vcm-logo-placeholder">N/A</span>
                    )}
                  </td>
                  <td className="text-end">
                    <span> {company.category_count || 0}</span>
                  </td>
                  <td>
                    <FaUsers
                      size={16}
                      className="action-icon text-primary me-3"
                      style={{ cursor: "pointer" }}
                      onClick={() => handleViewAdmins(company.company_code)}
                      title="View Admins"
                    />
                    <FaEdit
                      size={16}
                      className="vcm-action-icon me-3"
                      style={{ cursor: "pointer", color: "#007bff" }}
                      onClick={() =>
                        navigate(
                          `/vcm-admin/edit-company/${company.company_id || company.id}`,
                        )
                      }
                      title="Edit Company"
                    />
                    <FaKey
                      size={16}
                      className="vcm-action-icon text-secondary"
                      style={{ cursor: "pointer" }}
                      onClick={() => handleManageLicense(company)}
                      title="Manage License"
                    />
                  </td>
                </tr>
              ))}

              {companies.length === 0 && (
                <tr>
                  <td colSpan="10" className="text-center text-muted py-4">
                    No companies found.{" "}
                    <Button
                      variant="link"
                      onClick={() => navigate("/vcm-admin/register-company")}
                    >
                      Add your first company
                    </Button>
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      )}

      {/* License Management Modal */}
      <Modal show={showLicenseModal} onHide={closeLicenseModal} centered>
        <Modal.Header closeButton>
          <Modal.Title>
            <FaKey className="me-2" />
            Manage License - {selectedCompany?.company_name}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {licenseError && (
            <Alert variant="danger" className="mb-3">
              {licenseError}
            </Alert>
          )}
          {licenseSuccess && (
            <Alert variant="success" className="mb-3">
              {licenseSuccess}
            </Alert>
          )}

          <Form onSubmit={handleLicenseSubmit}>
            <div className="mb-3">
              <strong>Company Code:</strong> {selectedCompany?.company_code}
            </div>

            <Form.Group className="mb-3">
              <Form.Label>Category Count</Form.Label>
              <Form.Control
                type="number"
                min="0"
                value={licenseData.category_count}
                onChange={(e) =>
                  setLicenseData({
                    ...licenseData,
                    category_count: e.target.value,
                  })
                }
                placeholder="Enter number of categories allowed"
                required
              />
              <Form.Text className="text-muted">
                Set the number of product categories this company is licensed
                for.
              </Form.Text>
            </Form.Group>

            <div className="d-flex justify-content-end gap-2">
              <Button
                variant="secondary"
                onClick={closeLicenseModal}
                disabled={licenseLoading}
              >
                Cancel
              </Button>
              <Button type="submit" variant="primary" disabled={licenseLoading}>
                {licenseLoading ? (
                  <>
                    <Spinner animation="border" size="sm" className="me-2" />
                    Updating...
                  </>
                ) : (
                  "Update License"
                )}
              </Button>
            </div>
          </Form>
        </Modal.Body>
      </Modal>
    </div>
  );
}
